/**
 * Onboarding Service
 *
 * API client for identity user onboarding.
 * Routes:
 * - GET/POST /orgs/{orgId}/identity/users/{userId}/onboarding
 * - GET /orgs/{orgId}/identity/onboarding-statuses
 */

import { apiClient } from "@/services/shared/infrastructure";
import { makeService } from "@/services/shared/infrastructure/service-wrapper";
import { getEffectiveOrgId } from "@/services/shared/infrastructure/tenant-state";
import type { OnboardingStep } from "./identity.api-types";

type OnboardingStatusResponse = {
  completed?: boolean;
  steps?: Array<{
    id: string;
    name?: string;
    title?: string;
    description?: string;
    completed?: boolean;
    required?: boolean;
    order?: number;
    completedAt?: string;
  }>;
  currentStep?: string;
};

type CompleteStepResponse = {
  success?: boolean;
  stepId?: string;
  completed?: boolean;
  steps?: OnboardingStatusResponse["steps"];
};

export type OrgOnboardingStatusItem = {
  userId: string;
  userName?: string;
  userEmail?: string;
  completed: boolean;
  steps: Array<{
    id: string;
    name: string;
    completed: boolean;
    completedAt?: string;
  }>;
  currentStep?: string;
  completionPercentage: number;
  onboardingStartedAt?: string;
  onboardingCompletedAt?: string;
  lastActivityAt?: string;
  userCreatedAt?: string;
};

type OnboardingStatusesListApiResponse = {
  items: OrgOnboardingStatusItem[];
};

function getCurrentUserId(): string | null {
  const token = apiClient.getToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1] || ""));
    return (
      (payload.userId as string | undefined) ||
      (payload.sub as string | undefined) ||
      null
    );
  } catch {
    return null;
  }
}

function normalizeSteps(
  steps: OnboardingStatusResponse["steps"] = [],
): OnboardingStep[] {
  return steps.map((step, index) => ({
    id: String(step.id),
    title: String(step.title || step.name || step.id),
    description: step.description,
    completed: Boolean(step.completed),
    required: step.required ?? true,
    order: typeof step.order === "number" ? step.order : index + 1,
  }));
}

const rawOnboardingService = {
  async getStatus(): Promise<{ completed: boolean; steps: OnboardingStep[] }> {
    const orgId = getEffectiveOrgId();
    const userId = getCurrentUserId();
    if (!orgId) throw new Error("Organization ID is required");
    if (!userId) throw new Error("User ID is required");

    const response = await apiClient.get<{
      data?: OnboardingStatusResponse;
    } & OnboardingStatusResponse>(
      `/orgs/${orgId}/identity/users/${userId}/onboarding`,
    );
    const raw = response.data as
      | ({ data?: OnboardingStatusResponse } & OnboardingStatusResponse)
      | undefined;
    const payload = raw?.data ?? raw ?? {};
    const steps = normalizeSteps(payload.steps || []);
    return {
      completed: Boolean(payload.completed ?? steps.every((s) => s.completed)),
      steps,
    };
  },

  async completeStep(stepId: string): Promise<{ success: boolean }> {
    const orgId = getEffectiveOrgId();
    const userId = getCurrentUserId();
    if (!orgId) throw new Error("Organization ID is required");
    if (!userId) throw new Error("User ID is required");

    const response = await apiClient.post<
      { data?: CompleteStepResponse } & CompleteStepResponse
    >(`/orgs/${orgId}/identity/users/${userId}/onboarding`, { stepId });
    const raw = response.data as
      | ({ data?: CompleteStepResponse } & CompleteStepResponse)
      | undefined;
    const payload = raw?.data ?? raw ?? {};
    return { success: payload.success !== false };
  },

  /**
   * List org-wide user onboarding progress (admin tracking).
   */
  async listStatuses(
    params?: { cursor?: string; limit?: number },
    signal?: AbortSignal,
  ): Promise<{
    items: OrgOnboardingStatusItem[];
    nextCursor?: string;
  }> {
    const orgId = getEffectiveOrgId();
    if (!orgId) throw new Error("Organization ID is required");

    const response = await apiClient.get<OnboardingStatusesListApiResponse>(
      `/orgs/${orgId}/identity/onboarding-statuses`,
      { params, signal },
    );
    const items = response.data?.items || [];
    const nextCursor =
      response.meta?.pagination?.nextCursor || undefined;

    return { items, nextCursor };
  },
};

export const onboardingService = makeService(
  rawOnboardingService,
  "onboarding",
);
